import validator from "validator";
import { nameError } from "./names";

/**
 * The messages and rules every web form uses for a person's details (#274): the same sentence
 * for the same problem on every screen (sign-up, Login, Forgot Password, Contact Us, Profile,
 * Change / Update Password, Add Admin). They used to be typed into each form, so one field read
 * "Please Enter a Frist name.", "Please Enter a Email." or "Password and confirm Password does
 * not match" depending on the page.
 */

/** The message under an empty required field, e.g. requiredError("first name"). */
export const requiredError = (field) => `Please enter your ${field}.`;

export const EMAIL_REQUIRED = requiredError("email");
export const EMAIL_INVALID = "Please enter a valid email.";
export const PASSWORD_REQUIRED = requiredError("password");
export const CURRENT_PASSWORD_REQUIRED = requiredError("current password");
export const NEW_PASSWORD_REQUIRED = "Please enter a new password.";
export const CONFIRM_PASSWORD_REQUIRED = "Please confirm your password.";
export const PASSWORDS_MISMATCH = "Passwords do not match.";
export const PHONE_INVALID = "Please enter a 10-digit phone number.";
export const ZIP_INVALID = "Please enter a 5-digit zip code.";
export const MESSAGE_REQUIRED = "Please enter a message.";
/** Under an empty date-and-time picker (Resale Bid, Generate Promo Code). */
export const DATE_TIME_REQUIRED = "Please select the date and time.";
/** Under the Resale Bid dialog's empty ownership select (#292). */
export const SELL_SHARE_REQUIRED = "Please select the ownership share to sell.";

/**
 * The rule for a new password: at least 8 characters, with an uppercase letter, a lowercase
 * letter, a digit and one of # ? ! @ $ % ^ & * -. The API applies the same expression to every
 * input that sets one (PasswordRule.cs), so change both together. Login checks no rule: a
 * password set under the old 6-character sign-up rule still signs in.
 */
export const PASSWORD_REGEX = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

/**
 * PASSWORD_REGEX for an input's `pattern`. The browser anchors it and compiles it with the `v`
 * flag, where a hyphen inside a class must be escaped.
 */
export const PASSWORD_PATTERN = "(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*\\-]).{8,}";

export const PASSWORD_RULE_ERROR =
  "Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number and one of # ? ! @ $ % ^ & * -.";

/** The rule, shown under a new-password field before anything is typed (#258). */
export const PASSWORD_HINT =
  "At least 8 characters, including an uppercase letter, a lowercase letter, a number and one of # ? ! @ $ % ^ & * -.";

/** True when `password` follows the rule. An empty or missing password is not valid. */
export const isValidPassword = (password) => PASSWORD_REGEX.test(String(password ?? ""));

/**
 * True for an address with a domain and a top-level domain. `type="email"` alone accepts "a@b",
 * which no mail server delivers to.
 */
export const isValidEmail = (email) => validator.isEmail(String(email ?? "").trim());

/** The message under an invalid email field: empty, or not an address. */
export const emailError = (email) => (email ? EMAIL_INVALID : EMAIL_REQUIRED);

/** The message under an invalid name field, e.g. personNameError("first name", value). */
export const personNameError = (field, value) => (value ? nameError(field) : requiredError(field));

/** The message under an invalid new-password field. */
export const passwordError = (password, whenEmpty = PASSWORD_REQUIRED) =>
  password ? PASSWORD_RULE_ERROR : whenEmpty;

/** True when both are typed and differ; an empty confirmation is reported as empty instead. */
export const passwordsDiffer = (password, confirmation) =>
  !!password && !!confirmation && password !== confirmation;

/** The youngest a player may be: Profile's Date of Birth offers no later birthday. */
export const ADULT_AGE = 18;

/**
 * The latest birth date of someone who is ADULT_AGE on `today`, at today's time of day. On
 * Feb 29 it is Feb 28 (that year has no Feb 29), so a player born Mar 1 still waits a day.
 */
export const latestAdultBirthDate = (today = new Date()) => {
  const latest = new Date(today);
  latest.setFullYear(today.getFullYear() - ADULT_AGE);
  if (latest.getMonth() !== today.getMonth()) latest.setDate(0); // Mar 1 back to Feb 28
  return latest;
};

/** True when someone born on `date` (at any time of that day) is not yet ADULT_AGE on `today`. */
export const isUnderAdultAge = (date, today = new Date()) => {
  const latest = latestAdultBirthDate(today);
  const day = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return day(date) > day(latest);
};
